import { useCallback, useEffect, useState } from "react";
import { Cloud } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { clearRestoreCache } from "@/lib/restore";
import { useAuth } from "@/context/AuthContext";
import { OneDriveSection } from "@/components/OneDriveSection";

const KEY = "rh.onedriveEnabled";
export const onedriveEnabled = () => localStorage.getItem(KEY) === "1";

export const OneDriveToggleSection = () => {
  const { user } = useAuth();
  const [on, setOn] = useState(onedriveEnabled);
  const [connected, setConnected] = useState(false);
  const onStatus = useCallback((st) => setConnected(!!st?.connected), []);
  useEffect(() => { localStorage.setItem(KEY, on ? "1" : "0"); }, [on]);
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("onedrive")) setOn(true);
  }, []);
  if (!user?.permissions?.upload_onedrive) return null;
  const toggle = async (v) => {
    if (!v && connected && window.confirm("Also disconnect OneDrive and remove its songs from your library? Cancel keeps the connection but hides the panel.")) {
      try { const { data } = await api.delete("/onedrive"); clearRestoreCache(); setConnected(false); toast.success(data.tracks ? `OneDrive disconnected — removed ${data.tracks} song${data.tracks === 1 ? "" : "s"}` : "OneDrive disconnected"); }
      catch (e) { toast.error(formatApiError(e)); return; }
    }
    setOn(v);
  };
  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-8" data-testid="onedrive-toggle-section">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-md ${on ? "bg-sky-600 text-white" : "bg-zinc-100 text-zinc-500"}`}><Cloud size={18} /></div>
          <div>
            <h2 className="flex items-center gap-2 text-lg font-medium">OneDrive <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-600">Backup · optional</span></h2>
            <p className="text-xs text-zinc-500" data-testid="onedrive-toggle-hint">{on ? "On — connect a OneDrive music folder below as a backup source. Nothing connects until you press Connect." : "Off — OneDrive stays disconnected. Turn on to show the connection panel."}</p>
          </div>
        </div>
        <Switch checked={on} onCheckedChange={toggle} aria-label="OneDrive on/off" data-testid="onedrive-toggle-switch" />
      </div>
      {on && <div className="mt-6 -mx-8 -mb-8 border-t border-zinc-100 [&>section]:rounded-t-none [&>section]:border-0" data-testid="onedrive-toggle-body"><OneDriveSection onChange={onStatus} /></div>}
    </section>
  );
};
